import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const AgentAdoptionTrends = ({ dateRange, refreshTrigger }) => {
  const { user } = useAuth();
  const [adoptionData, setAdoptionData] = useState({
    timeSeriesData: [],
    agentPopularity: [],
    engagementMetrics: []
  });
  const [loading, setLoading] = useState(true);
  const [activeChart, setActiveChart] = useState('timeSeries');

  useEffect(() => {
    loadAdoptionData();

    // Set up real-time subscription
    const channel = supabase?.channel('agent_adoption')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'agent_usage_metrics'
        },
        () => {
          loadAdoptionData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [dateRange, refreshTrigger, user]);

  const getDateFilter = () => {
    const now = new Date();
    let startDate;

    switch (dateRange) {
      case '24h':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        return null;
    }

    return startDate?.toISOString();
  };

  const loadAdoptionData = async () => {
    setLoading(true);
    try {
      let startDate = getDateFilter();

      // Build query with date filter
      let query = supabase?.from('agent_usage_metrics')?.select('*')?.order('created_at', { ascending: true });

      if (startDate) {
        query = query?.gte('created_at', startDate);
      }

      const { data: usageData, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Error loading adoption data:', error?.message);
          setAdoptionData({ timeSeriesData: [], agentPopularity: [], engagementMetrics: [] });
          return;
        }
      }

      if (usageData && usageData?.length > 0) {
        processUsageData(usageData);
      } else {
        setAdoptionData({ timeSeriesData: [], agentPopularity: [], engagementMetrics: [] });
      }
    } catch (error) {
      console.error('Error loading adoption data:', error);
      setAdoptionData({ timeSeriesData: [], agentPopularity: [], engagementMetrics: [] });
    } finally {
      setLoading(false);
    }
  };

  const isSchemaError = (error) => {
    if (!error) return false;
    if (error?.code && typeof error?.code === 'string') {
      const errorClass = error?.code?.substring(0, 2);
      if (errorClass === '42' || errorClass === '08') return true;
    }
    return false;
  };

  const processUsageData = (usageData) => {
    // Group by date for time series
    const timeSeriesMap = {};
    const agentPopularityMap = {};
    const engagementMap = {};

    usageData?.forEach(metric => {
      const date = new Date(metric?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const agent = metric?.agent_id;

      // Time series data - group by date and agent
      if (!timeSeriesMap?.[date]) {
        timeSeriesMap[date] = { 
          date, 
          'marketing-ops': 0, 
          'personal-productivity': 0, 
          'feedback-analytics': 0,
          'onboarding': 0
        };
      }
      if (agent && timeSeriesMap?.[date]?.[agent] !== undefined) {
        timeSeriesMap[date][agent]++;
      }

      // Agent popularity - total usage count per agent
      agentPopularityMap[agent] = (agentPopularityMap?.[agent] || 0) + 1;

      // Engagement metrics - detailed breakdown per agent
      if (!engagementMap?.[agent]) {
        engagementMap[agent] = { 
          agent, 
          interactions: 0, 
          tasksCompleted: 0, 
          contentGenerated: 0,
          avgEfficiency: 0,
          efficiencyCount: 0
        };
      }
      
      if (metric?.metric_type === 'interaction') engagementMap[agent].interactions++;
      if (metric?.metric_type === 'task_completed') engagementMap[agent].tasksCompleted++;
      if (metric?.metric_type === 'content_generated') engagementMap[agent].contentGenerated++;
      
      if (metric?.efficiency_score) {
        engagementMap[agent].avgEfficiency += parseFloat(metric?.efficiency_score);
        engagementMap[agent].efficiencyCount++;
      }
    });

    // Calculate average efficiency
    Object.values(engagementMap)?.forEach(agent => {
      if (agent?.efficiencyCount > 0) {
        agent.avgEfficiency = (agent?.avgEfficiency / agent?.efficiencyCount)?.toFixed(1);
      }
      delete agent?.efficiencyCount;
    });

    // Format agent names for display
    const formatAgentName = (agentId) => {
      const nameMap = {
        'marketing-ops': 'Marketing',
        'personal-productivity': 'Productivity',
        'feedback-analytics': 'Feedback',
        'onboarding': 'Onboarding'
      };
      return nameMap?.[agentId] || agentId;
    };

    setAdoptionData({
      timeSeriesData: Object.values(timeSeriesMap),
      agentPopularity: Object.entries(agentPopularityMap)
        ?.map(([agent, count]) => ({
          agent: formatAgentName(agent),
          usage: count
        }))
        ?.sort((a, b) => b?.usage - a?.usage),
      engagementMetrics: Object.values(engagementMap)?.map(metric => ({
        ...metric,
        agent: formatAgentName(metric?.agent)
      }))
    });
  };

  const getMostPopularAgent = () => {
    if (adoptionData?.agentPopularity?.length === 0) return 'N/A';
    return adoptionData?.agentPopularity?.[0]?.agent || 'N/A';
  };

  const getLeastPopularAgent = () => {
    if (adoptionData?.agentPopularity?.length === 0) return 'N/A';
    return adoptionData?.agentPopularity?.[adoptionData?.agentPopularity?.length - 1]?.agent || 'N/A';
  };

  const getTotalInteractions = () => {
    return adoptionData?.engagementMetrics?.reduce((sum, agent) => sum + agent?.interactions, 0);
  };

  const charts = [
    { id: 'timeSeries', label: 'Usage Over Time', icon: 'TrendingUp' },
    { id: 'popularity', label: 'Agent Popularity', icon: 'BarChart3' },
    { id: 'engagement', label: 'Engagement Metrics', icon: 'Activity' }
  ];

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-warm p-6">
        <p className="text-muted-foreground text-center">Loading adoption trends...</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm p-6 space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Award" size={20} color="var(--color-green-500)" />
            <span className="text-sm text-muted-foreground font-caption">Most Popular</span>
          </div>
          <p className="text-xl font-semibold text-foreground font-heading">{getMostPopularAgent()}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingDown" size={20} color="var(--color-orange-500)" />
            <span className="text-sm text-muted-foreground font-caption">Least Popular</span>
          </div>
          <p className="text-xl font-semibold text-foreground font-heading">{getLeastPopularAgent()}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Activity" size={20} color="var(--color-primary)" />
            <span className="text-sm text-muted-foreground font-caption">Total Interactions</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-heading">{getTotalInteractions()}</p>
        </div>
      </div>

      {/* Chart Tabs */}
      <div className="flex gap-2 border-b border-border">
        {charts?.map((chart) => (
          <button
            key={chart?.id}
            onClick={() => setActiveChart(chart?.id)}
            className={`
              flex items-center gap-2 px-4 py-2 font-caption font-medium transition-colors
              ${activeChart === chart?.id 
                ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }
            `}
          >
            <Icon name={chart?.icon} size={18} />
            {chart?.label}
          </button>
        ))}
      </div>

      {/* Charts */}
      <div className="h-80">
        {adoptionData?.timeSeriesData?.length === 0 && adoptionData?.agentPopularity?.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">No adoption data available for the selected date range</p>
          </div>
        ) : (
          <>
            {activeChart === 'timeSeries' && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={adoptionData?.timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
                  <YAxis stroke="var(--color-muted-foreground)" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-card)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }} 
                  />
                  <Legend />
                  <Line type="monotone" dataKey="onboarding" stroke="var(--color-primary)" name="Onboarding" strokeWidth={2} />
                  <Line type="monotone" dataKey="marketing-ops" stroke="var(--color-blue-500)" name="Marketing" strokeWidth={2} />
                  <Line type="monotone" dataKey="personal-productivity" stroke="var(--color-green-500)" name="Productivity" strokeWidth={2} />
                  <Line type="monotone" dataKey="feedback-analytics" stroke="var(--color-orange-500)" name="Feedback" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}

            {activeChart === 'popularity' && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={adoptionData?.agentPopularity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="agent" stroke="var(--color-muted-foreground)" />
                  <YAxis stroke="var(--color-muted-foreground)" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-card)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }} 
                  />
                  <Legend />
                  <Bar dataKey="usage" fill="var(--color-primary)" name="Usage Count" />
                </BarChart>
              </ResponsiveContainer>
            )}

            {activeChart === 'engagement' && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={adoptionData?.engagementMetrics}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="agent" stroke="var(--color-muted-foreground)" />
                  <YAxis stroke="var(--color-muted-foreground)" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-card)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }} 
                  />
                  <Legend />
                  <Bar dataKey="interactions" fill="var(--color-blue-500)" name="Interactions" />
                  <Bar dataKey="tasksCompleted" fill="var(--color-green-500)" name="Tasks Completed" />
                  <Bar dataKey="contentGenerated" fill="var(--color-orange-500)" name="Content Generated" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AgentAdoptionTrends;